// ============================================================================
// src/components/BenchmarkPanel/DistributionChart.tsx
// PV-1052 · PR-G — SVG horizontal bar showing the DLD price/sqft distribution
// for the winning cascade level, with deal position highlighted.
//
// Design : zone [p25, p75] = bronze (market range), queues [min, p25] /
// [p75, max] = neutral, central tendency (median or mean per display_stat)
// = bronze line, deal_pps = triangle ▼ colored per relative position.
//
// Accessibility : role="img" + aria-label descriptive (range + deal position).
// SVG is responsive via 100% width + viewBox.
// ============================================================================

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '@/lib/format';
import { dealMarkerTone } from './DistributionChart.utils';
import './DistributionChart.css';

export type DistributionChartProps = {
  min: number;
  p25: number;
  p75: number;
  max: number;
  /** Median or mean depending on active display_stat (the RPC computes both). */
  central: number;
  /** The deal's own price_per_sqft. */
  deal: number;
  displayStat: 'median' | 'mean';
};

// Layout constants (matches DistributionChart.css width via viewBox).
const VIEWBOX_W = 320;
const VIEWBOX_H = 80;
const AXIS_X_MIN = 28; // left padding for label
const AXIS_X_MAX = 292; // right padding for label
const AXIS_Y = 38;
const BAR_HEIGHT = 14;

/** Map a value within [min, max] to its X coordinate on the axis. Clamps to bounds. */
function valueToX(value: number, min: number, max: number): number {
  if (max === min) return AXIS_X_MIN;
  const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return AXIS_X_MIN + ratio * (AXIS_X_MAX - AXIS_X_MIN);
}

export function DistributionChart({
  min,
  p25,
  p75,
  max,
  central,
  deal,
  displayStat,
}: DistributionChartProps) {
  const { t } = useTranslation();

  const positions = useMemo(() => {
    return {
      min: valueToX(min, min, max),
      p25: valueToX(p25, min, max),
      central: valueToX(central, min, max),
      p75: valueToX(p75, min, max),
      max: valueToX(max, min, max),
      deal: valueToX(deal, min, max),
    };
  }, [min, p25, p75, max, central, deal]);

  const tone = dealMarkerTone(deal, central);

  const ariaLabel = t('common:benchmark.distribution.aria_label', {
    deal: formatNumber(Math.round(deal)),
    min: formatNumber(Math.round(min)),
    max: formatNumber(Math.round(max)),
  });

  const centralLabel =
    displayStat === 'mean'
      ? t('common:benchmark.distribution.mean')
      : t('common:benchmark.distribution.median');

  return (
    <figure className="dld-distribution-chart" aria-labelledby="dld-dist-title">
      <figcaption id="dld-dist-title" className="dld-distribution-chart__title">
        {t('common:benchmark.distribution.title')}
      </figcaption>
      <svg
        className="dld-distribution-chart__svg"
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={ariaLabel}
      >
        {/* Queue [min, p25] — neutral */}
        <rect
          className="dld-distribution-chart__queue"
          x={positions.min}
          y={AXIS_Y - BAR_HEIGHT / 2}
          width={Math.max(0, positions.p25 - positions.min)}
          height={BAR_HEIGHT}
        />
        {/* Market range [p25, p75] — bronze */}
        <rect
          className="dld-distribution-chart__range"
          x={positions.p25}
          y={AXIS_Y - BAR_HEIGHT / 2}
          width={Math.max(0, positions.p75 - positions.p25)}
          height={BAR_HEIGHT}
        />
        {/* Queue [p75, max] — neutral */}
        <rect
          className="dld-distribution-chart__queue"
          x={positions.p75}
          y={AXIS_Y - BAR_HEIGHT / 2}
          width={Math.max(0, positions.max - positions.p75)}
          height={BAR_HEIGHT}
        />
        {/* Central tendency marker (median or mean) — vertical line */}
        <line
          className="dld-distribution-chart__central"
          x1={positions.central}
          y1={AXIS_Y - BAR_HEIGHT / 2 - 4}
          x2={positions.central}
          y2={AXIS_Y + BAR_HEIGHT / 2 + 4}
        />
        {/* Deal marker — triangle ▼ */}
        <polygon
          className={`dld-distribution-chart__deal dld-distribution-chart__deal--${tone}`}
          points={`${positions.deal - 5},${AXIS_Y - BAR_HEIGHT / 2 - 14} ${positions.deal + 5},${AXIS_Y - BAR_HEIGHT / 2 - 14} ${positions.deal},${AXIS_Y - BAR_HEIGHT / 2 - 4}`}
        />
        {/* Edge labels — min + max */}
        <text
          className="dld-distribution-chart__label dld-distribution-chart__label--edge"
          x={positions.min}
          y={AXIS_Y + BAR_HEIGHT / 2 + 18}
          textAnchor="start"
        >
          {formatNumber(Math.round(min))}
        </text>
        <text
          className="dld-distribution-chart__label dld-distribution-chart__label--edge"
          x={positions.max}
          y={AXIS_Y + BAR_HEIGHT / 2 + 18}
          textAnchor="end"
        >
          {formatNumber(Math.round(max))}
        </text>
        {/* Central tendency value above the line */}
        <text
          className="dld-distribution-chart__label dld-distribution-chart__label--central"
          x={positions.central}
          y={AXIS_Y - BAR_HEIGHT / 2 - 10}
          textAnchor="middle"
        >
          {formatNumber(Math.round(central))}
        </text>
      </svg>
      <div className="dld-distribution-chart__legend">
        <span className="dld-distribution-chart__legend-item">
          <span className="dld-distribution-chart__legend-swatch dld-distribution-chart__legend-swatch--range" />
          {t('common:benchmark.distribution.legend_range')}
        </span>
        <span className="dld-distribution-chart__legend-item">
          <span className="dld-distribution-chart__legend-swatch dld-distribution-chart__legend-swatch--central" />
          {centralLabel}
        </span>
        <span className="dld-distribution-chart__legend-item">
          <span
            className={`dld-distribution-chart__legend-swatch dld-distribution-chart__legend-swatch--deal dld-distribution-chart__legend-swatch--deal-${tone}`}
          />
          {t('common:benchmark.distribution.your_deal')}
        </span>
      </div>
    </figure>
  );
}
